import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/apiFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, User2, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const fetchJson = async (...args: any[]) => {
  const res = await apiFetch(...args);
  if (res && typeof (res as Response).json === "function") {
    const r = res as Response;
    if (!r.ok) {
      let body = "";
      try { body = await r.text(); } catch {}
      throw new Error(`HTTP ${r.status} ${r.statusText || ""}${body ? " — " + body : ""}`);
    }
    return r.json();
  }
  return res;
};

function StatusBadge({ value }: { value: any }) {
  const normalized =
    value === 1 || value === true ? "active"
    : value === 0 || value === false ? "inactive"
    : typeof value === "string" ? value.toLowerCase()
    : "";

  const variant =
    normalized === "active" ? "bg-green-100 text-green-800 border-green-200"
    : normalized === "inactive" || normalized === "disabled" ? "bg-gray-100 text-gray-800 border-gray-200"
    : "bg-blue-100 text-blue-800 border-blue-200";

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${variant}`}>
      {normalized || "—"}
    </span>
  );
}

type UserRow = {
  id: number;
  name: string;
  username: string;
  email: string;
  role?: "admin" | "user" | string;
  status?: string | number | boolean;
  updated_at?: string;
};

export default function Users() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  // unified modal (add+edit)
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [formData, setFormData] = useState<{ name: string; email: string; role: "admin" | "user" }>({
    name: "",
    email: "",
    role: "user",
  });
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!editingUser;
  const { toast } = useToast();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await fetchJson("/api/users", { signal: controller.signal });
        if (!isMounted) return;
        setUsers(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (err?.name === "AbortError") return;

        if (err.message === "Forbidden") {
          navigate("/dashboard/projects", { replace: true });
          return;
        }

        if (isMounted) {
          setError(err?.message || "Failed to load users");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [isAuthenticated, navigate]);


  const rows = useMemo(() => users ?? [], [users]);
  const formatDateTime = (value?: string) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

    function getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
        return match ? match[2] : null;
    }

  function openCreate() {
    setEditingUser(null);
    setFormData({ name: "", email: "", role: "user" });
    setModalOpen(true);
  }

  function openEdit(u: UserRow) {
    setEditingUser(u);
    setFormData({
      name: u.name || "",
      email: u.email || "",
      role: (u.role === "admin" ? "admin" : "user"),
    });
    setModalOpen(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (isEdit && editingUser) {
        // Sanctum CSRF cookie (if you’re using it)
        await fetch("/sanctum/csrf-cookie", { credentials: "include" });

        // XSRF header is helpful with Sanctum; DO NOT set Content-Type manually=
        const headers: HeadersInit = { Accept: "application/json", "Content-Type": "application/json" };
        const xsrfToken = getCookie("XSRF-TOKEN");
        if (xsrfToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);

        // UPDATE
        const updated = await fetch(`/api/users/${editingUser.id}`, {
          method: "PUT",
          credentials: "include",
          headers,
          body: JSON.stringify(formData),
        });
        setUsers((prev) => prev.map((u) => (u.id === editingUser.id ? { ...u, ...updated } : u)));
      } else {
        // Sanctum CSRF cookie (if you’re using it)
        await fetch("/sanctum/csrf-cookie", { credentials: "include" });

        // XSRF header is helpful with Sanctum; DO NOT set Content-Type manually=
        const headers: HeadersInit = { Accept: "application/json", "Content-Type": "application/json" };
        const xsrfToken = getCookie("XSRF-TOKEN");
        if (xsrfToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);

        // CREATE (invite) – backend should send email w/ link to set password
        const created = await fetch("/api/users", {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify(formData),
        });

        let data: any = null;
        try { data = await created.json(); } catch { /* ignore */ }
        if (created.status === 422){
            const msg =
                data?.message ??
                "Invalid Email";

            toast({
                variant: "destructive",
                title: "Couldn't create user",
                description: msg,
            });
            return;
        }

        setUsers((prev) => [data, ...prev]);
        toast({
            title: "Success",
            description: "User was successfully created",
        });
      }
      setModalOpen(false);
      setEditingUser(null);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || (isEdit ? "Failed to update user" : "Failed to create user"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <User2 className="h-5 w-5" />
          <h1 className="text-3xl font-bold text-foreground">Users</h1>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New User
        </Button>
      </div>

      {/* Error */}
      {error && (
        <Card className="border border-red-200">
          <CardContent className="p-3 text-sm text-red-700 bg-red-50">{error}</CardContent>
        </Card>
      )}

      {/* List */}
      <Card className="border border-border">
        <CardHeader className="p-4">
          <CardTitle className="text-lg">All Users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">
              <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-t-2 border-b-2 border-primary" />
              Loading users…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">No users found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-medium">Full Name</th>
                    <th className="px-4 py-2 font-medium">Username</th>
                    <th className="px-4 py-2 font-medium">Email</th>
                    <th className="px-4 py-2 font-medium">Role</th>
                    <th className="px-4 py-2 font-medium">Date Updated</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((user) => (
                    <tr key={user.id ?? user.email} className="border-t border-border hover:bg-accent">
                      <td className="px-4 py-2">
                          {user?.name ?? "—"}
                      </td>
                      <td className="px-4 py-2">{user?.username ?? "—"}</td>
                      <td className="px-4 py-2">{user?.email ?? "—"}</td>
                      <td className="px-4 py-2">{(user?.role || "user").toString()}</td>
                      <td className="px-4 py-2">{formatDateTime(user?.updated_at)}</td>
                      <td className="px-4 py-2">
                        <Badge variant="secondary" className="bg-transparent border">
                          <StatusBadge value={user?.status} />
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(user)} title="Edit User">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unified Add/Edit Dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit User" : "Invite New User"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) => setFormData((s) => ({ ...s, name: e.target.value }))}
                required
              />
              <Input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData((s) => ({ ...s, email: e.target.value }))}
                required
              />
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Role</label>
                <select
                  className="w-full border rounded-md h-10 px-3 bg-background"
                  value={formData.role}
                  onChange={(e) => setFormData((s) => ({ ...s, role: e.target.value as "admin" | "user" }))}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            {!isEdit && (
              <p className="text-xs text-muted-foreground">
                After inviting, the user will receive an email with a secure link to set their password and sign in.
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (isEdit ? "Saving…" : "Inviting…") : (isEdit ? "Save" : "Send Invite")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
